/**
 * pageLabel — the short name of the page shown at a given path. The header
 * breadcrumb and the browser tab title both read it, so those two surfaces
 * cannot name the same page differently. The left nav is NOT a consumer: it
 * hardcodes its own link text and has already drifted (`Health` for what this
 * calls `Fleet Health`, `📘 User Guide` for `User Guide`).
 *
 * Every path gets a label, including ones no rule recognises: they fall to the
 * catch-all's own label. That makes each rule a claim about what the router
 * renders there, so the rules have to match the way the router matches —
 * case-insensitively, on a percent-decoded path, with trailing slashes ignored,
 * and with a single `:id` matching exactly one segment. A rule looser than its
 * route labels the header with a section the page is not showing. `test/pageLabelRouteParity.contract.test.ts`
 * measures that agreement against the real route table rather than trusting this
 * comment.
 *
 * The rules are ONE ordered table, matched top to bottom, so adding a route
 * means adding a row — there is no second shape to choose between and no label
 * spelled out anywhere but the block of consts above it.
 */

/**
 * What the `*` catch-all's page is called. `/` maps to it too, because the
 * catch-all renders that same component (FleetHealth) — so this is not a guess
 * about an unrecognised path, it is the page on screen. Exported for the parity
 * spec, which asserts the biconditional against it.
 */
export const CATCH_ALL_LABEL = 'Fleet Health'

const ACTIVITY_LABEL = 'Activity'
const APPROVALS_LABEL = 'Approvals'
const IMPROVEMENTS_LABEL = 'Improvements'
const INVESTIGATIONS_LABEL = 'Investigations'
const FEEDBACK_LABEL = 'Feedback'
const LOGS_LABEL = 'Logs'
const DIAGNOSTICS_LABEL = 'Diagnostics'
const LLM_USAGE_LABEL = 'LLM Usage'
// ADR-167 D1 — what this fleet is doing right now.
const IN_FLIGHT_LABEL = 'In flight'
const CHAT_LABEL = 'Chat'
// Not "Core Updates" any more: the page now leads with this fleet's OWN changes
// waiting to go live (ADR-122 D2), with platform currency below it.
const UPDATES_LABEL = 'Updates'
const USER_GUIDE_LABEL = 'User Guide'
// One label for the whole Fleet Editor section: the breadcrumb does not need
// per-facet detail (ADR-111 IA-6).
const FLEET_EDITOR_LABEL = 'Fleet Editor'
/** ADR-177 D3 — the run history and one run's phase tree. */
const AUTHORING_RUNS_LABEL = 'Authoring runs'
// What `/modules/:id` and `/plugins/:id` read before the list arrives, or when
// the id is not in it — the same generic name ModulePage/PluginPage shows.
const MODULE_LABEL = 'Module'
const PLUGIN_LABEL = 'Plugin'
// ADR-210 — the page behind a permanent file link.
const FILE_LABEL = 'File'

/**
 * One entry of the fetched module or plugin list, reduced to the two fields a
 * label needs. Structural on purpose: taking `ModuleSummary`/`PluginSummary`
 * would make every caller and every test of this file import api/client, whose
 * module-load `new MCClient()` has no business in a unit spec. AppShell maps
 * `module_id` to `id` at the call site.
 */
export interface TitledEntity {
  id: string
  title: string
}

interface PageEntities {
  modules: TitledEntity[]
  plugins: TitledEntity[]
}

interface Rule {
  /**
   * Anchored and case-insensitive, matched against the pathname with trailing
   * slashes stripped. No `g` flag: these are module-level and `exec` would carry
   * a lastIndex between calls.
   */
  pattern: RegExp
  /** A fixed label, or one the fetched lists decide. */
  label: string | ((match: RegExpExecArray, entities: PageEntities) => string)
}

/**
 * Every rule, in precedence order — the first match wins, and anything that
 * matches none falls to CATCH_ALL_LABEL.
 *
 * The Fleet Editor gets one row per route SHAPE in pages/fleetEditorRoutes.tsx
 * rather than one `/fleet-editor` prefix: only `fe-dataset` takes any depth, so a
 * prefix would claim `Fleet Editor` for `/fleet-editor/agents`, `/fleet-editor/foo`
 * and `/fleet-editor/foo/bar/baz` — all of which fall to the `*` catch-all and
 * render FleetHealth.
 */
const RULES: Rule[] = [
  // The two pages whose name is not in the path. The pattern is
  // case-insensitive but the captured id is NOT lowercased: ids are
  // case-sensitive, so a wrong-case id misses the list and falls back to the
  // generic label.
  {
    pattern: /^\/modules\/([^/]+)$/i,
    label: (match, entities) => titleIn(entities.modules, match[1]) || MODULE_LABEL,
  },
  {
    pattern: /^\/plugins\/([^/]+)$/i,
    label: (match, entities) => titleIn(entities.plugins, match[1]) || PLUGIN_LABEL,
  },

  // `/files/:scope/*` — a splat, so any depth under a scope (a path carries its own slashes).
  { pattern: /^\/files\/[^/]+\/.+$/i, label: FILE_LABEL },

  // App.tsx's /workflows and /workflows/* are <Navigate> to /fleet-editor and
  // render once at the source path, so they need the destination's label or they
  // flash the catch-all's. Anchored, so /workflows-old is not swept in. The splat
  // route takes any depth, which is why this one IS a prefix.
  { pattern: /^\/workflows(\/|$)/i, label: FLEET_EDITOR_LABEL },

  { pattern: /^\/fleet-editor$/i, label: FLEET_EDITOR_LABEL }, // fe-root
  // ADR-177 D3 — the authoring-run surfaces. Their OWN label, not the editor's: they
  // are separate pages with their own heading, and naming them "Fleet Editor" would
  // point an operator at a drawer that does not contain them.
  //
  // BEFORE the fe-legacy rule below, and this ordering is load-bearing:
  // /fleet-editor/runs/<id> is two segments under /fleet-editor and would otherwise be
  // swallowed by `:agent/:name`, labelling a run page as the editor.
  { pattern: /^\/fleet-editor\/runs$/i, label: AUTHORING_RUNS_LABEL },       // fe-runs
  { pattern: /^\/fleet-editor\/runs\/[^/]+$/i, label: AUTHORING_RUNS_LABEL }, // fe-run
  { pattern: /^\/fleet-editor\/(resources|data|files)$/i, label: FLEET_EDITOR_LABEL }, // fe-resources, fe-data, fe-files
  // fe-dataset is the one SPLAT in the editor's table (`/fleet-editor/data/*`): a
  // dataset name carries its own slashes (`org/asana-sections@v1`), so any depth
  // under /data is a real route and this rule has to take any depth too.
  { pattern: /^\/fleet-editor\/data\/.+$/i, label: FLEET_EDITOR_LABEL }, // fe-dataset
  { pattern: /^\/fleet-editor\/platform-skills(\/[^/]+)?$/i, label: FLEET_EDITOR_LABEL }, // fe-platform-skills, fe-platform-skill
  { pattern: /^\/fleet-editor\/[^/]+\/[^/]+$/i, label: FLEET_EDITOR_LABEL }, // fe-legacy `:agent/:name` — also absorbs fe-new
  { pattern: /^\/fleet-editor\/agents\/[^/]+\/[^/]+$/i, label: FLEET_EDITOR_LABEL }, // fe-facet
  // fe-entity `:agent/:facet/:name` — any facet, not just workflows. Three
  // segments deep and no further: /fleet-editor/agents/a/b/c/d matches no route.
  { pattern: /^\/fleet-editor\/agents\/[^/]+\/[^/]+\/[^/]+$/i, label: FLEET_EDITOR_LABEL }, // fe-entity

  // The routes whose whole path is fixed.
  { pattern: /^\/$/i, label: CATCH_ALL_LABEL },
  { pattern: /^\/activity$/i, label: ACTIVITY_LABEL },
  { pattern: /^\/approvals$/i, label: APPROVALS_LABEL },
  { pattern: /^\/improvements$/i, label: IMPROVEMENTS_LABEL },
  { pattern: /^\/investigations$/i, label: INVESTIGATIONS_LABEL },
  { pattern: /^\/feedback$/i, label: FEEDBACK_LABEL },
  { pattern: /^\/logs$/i, label: LOGS_LABEL },
  { pattern: /^\/diagnostics$/i, label: DIAGNOSTICS_LABEL },
  { pattern: /^\/llm-usage$/i, label: LLM_USAGE_LABEL },
  { pattern: /^\/llm-usage\/(traces|trace)$/i, label: LLM_USAGE_LABEL },
  { pattern: /^\/in-flight$/i, label: IN_FLIGHT_LABEL },
  { pattern: /^\/chat$/i, label: CHAT_LABEL },
  { pattern: /^\/updates$/i, label: UPDATES_LABEL },
  { pattern: /^\/help$/i, label: USER_GUIDE_LABEL },

  // App.tsx's two detail routes. `[^/]+` is one segment, matching the single
  // `:id` in each — so /improvements/x/pr/extra and /investigations/a/b match no
  // route and must not be labelled here.
  { pattern: /^\/improvements\/[^/]+\/pr$/i, label: IMPROVEMENTS_LABEL },
  { pattern: /^\/investigations\/[^/]+$/i, label: INVESTIGATIONS_LABEL },
]

export function pageLabel(
  pathname: string,
  modules: TitledEntity[],
  plugins: TitledEntity[],
): string {
  // Rules match EXACTLY, after decoding the path the way the router decodes it
  // and stripping trailing slashes. React Router's compiled patterns end in
  // `\/*$`, so it accepts a RUN of them: `/logs//` renders Logs. `|| '/'` keeps
  // the root a path rather than the empty string.
  const path = decodePath(pathname).replace(/\/+$/, '') || '/'

  for (const rule of RULES) {
    const match = rule.pattern.exec(path)
    if (!match) continue
    return typeof rule.label === 'string' ? rule.label : rule.label(match, { modules, plugins })
  }

  // Anything the route table does not match falls to App.tsx's `*` route, which
  // renders FleetHealth. If that route is ever given a different component,
  // CATCH_ALL_LABEL is the one thing to change with it.
  return CATCH_ALL_LABEL
}

/**
 * The pathname as the router sees it. `useLocation().pathname` is percent-encoded,
 * and react-router matches its route patterns against a DECODED path — so
 * `/%6cogs` renders Logs and `/improvements/x/%70r` renders the PR review. Rules
 * matched against the raw pathname would label both with the catch-all's page.
 *
 * This mirrors react-router's own `decodePath`: decode each SEGMENT, then put back
 * any `/` the decode produced as `%2F`, so an encoded slash can never smuggle a
 * separator past an anchored rule — `/logs%2Ffoo` stays one segment and falls to
 * `*`, exactly as the router sends it there. A malformed `%` makes
 * decodeURIComponent throw, which here would throw during render; react-router
 * warns and matches the raw path, so this keeps the raw path.
 */
function decodePath(pathname: string): string {
  try {
    return pathname
      .split('/')
      .map(segment => decodeURIComponent(segment).replace(/\//g, '%2F'))
      .join('/')
  } catch {
    // Keep the raw path, as react-router does.
    return pathname
  }
}

/**
 * The entity's own title, or undefined when the list has not arrived or does not
 * hold that id. Callers use `||` rather than `??`: `title` is a non-optional
 * string from an unvalidated JSON cast (`ModuleSummary`, `PluginSummary`), so
 * `''` is representable and has to fall back too.
 *
 * The captured segment becomes an id the way react-router turns a capture group
 * into a param: `%2F` becomes `/`, and nothing else is decoded. decodePath has
 * already decoded the path once, and the router does NOT decode a second time on
 * top of that — it only puts back the slashes decodePath re-escaped. Measured
 * with matchRoutes against `/modules/:id`: `%2566leet-overview` gives the param
 * `%66leet-overview`, `a%252fb` gives `a%2fb`, `%2520` gives `%20`, and
 * `a%2Fb` gives `a/b`. A second decodeURIComponent here read those first three as
 * `fleet-overview`, `a/b` and a space, so the header and the tab could name a
 * module the page is not showing. The substitution is upper-case only and
 * cannot throw, matching the router on both counts — so a malformed `%` needs no
 * guard: it stays raw, misses the list, and falls back to the generic label.
 */
function titleIn(entities: TitledEntity[], rawId: string): string | undefined {
  const id = rawId.replace(/%2F/g, '/')
  return entities.find(entity => entity.id === id)?.title
}
